import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, getRedirectPath, user } = useAuth();
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = getRedirectPath(user.user_type);
      navigate(redirectPath);
    }
  }, [isAuthenticated, user, navigate, getRedirectPath]);

  // Load saved username from cookie (remember me)
  useEffect(() => {
    const cookies = document.cookie.split(';');
    const usernameCookie = cookies.find(c => c.trim().startsWith('username='));
    if (usernameCookie) {
      const savedUsername = usernameCookie.split('=')[1];
      if (savedUsername) {
        setUsername(savedUsername);
        setRememberMe(true);
      }
    }
  }, []);

  // Create bubbles on mount (matching PHP design)
  useEffect(() => {
    const bubbleSizes = [80, 100, 120, 150, 180, 200, 250];
    const bubblePositions = [
      { top: '10%', left: '5%' },
      { top: '20%', right: '10%' },
      { top: '40%', left: '15%' },
      { top: '60%', right: '5%' },
      { top: '80%', left: '10%' },
      { top: '30%', left: '80%' },
      { top: '70%', left: '85%' },
      { top: '85%', right: '15%' }
    ];

    for (let i = 0; i < 8; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'bubble';
      
      const size = bubbleSizes[Math.floor(Math.random() * bubbleSizes.length)];
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      
      const position = bubblePositions[i];
      Object.assign(bubble.style, position);
      
      document.body.appendChild(bubble);
    }

    return () => {
      // Cleanup bubbles on unmount
      const bubbles = document.querySelectorAll('.bubble');
      bubbles.forEach(bubble => bubble.remove());
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username.trim() || !password.trim()) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    const result = await login(username.trim(), password, rememberMe);

    if (result.success) {
      const redirectPath = getRedirectPath(result.user.user_type);
      navigate(redirectPath);
    } else {
      setError(result.message || 'Invalid username or password');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Welcome side - hidden on mobile */}
        <div className="welcome-side">
          <h2 className="h3 mb-4">Welcome to Qure</h2>
          <p className="mb-4">Access your research tools and protocols in one secure platform.</p>

          <div className="feature-list">
            <div className="feature-item">
              <i className="bi bi-clipboard-data"></i>
              <span>Protocol Management</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-badge-ar"></i>
              <span>AR & MR Simulations</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-people"></i>
              <span>Team Collaboration</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-graph-up"></i>
              <span>Progress Tracking</span>
            </div>
          </div>
        </div>

        {/* Form side */}
        <div className="form-side">
          <img 
            src="https://qure.science/images/qure.webp" 
            alt="Qure" 
            className="brand-logo"
            onError={(e) => {
              // Fallback if image path doesn't work
              e.target.src = 'https://via.placeholder.com/160x60?text=Qure';
            }}
          />
          
          <h1 className="form-title">Sign in to Qure</h1>
          <p className="form-subtitle">Welcome back! Please enter your details.</p>

          {error && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-circle-fill"></i>
              <span>{error}</span>
            </div>
          )}

          <form id="loginForm" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                placeholder="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-check mb-4">
              <input
                type="checkbox"
                className="form-check-input"
                id="remember_me"
                name="remember_me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="remember_me">
                Keep me signed in
              </label>
            </div>

            <div className="d-grid gap-3 mb-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right"></i>
                    Sign In
                  </>
                )}
              </button>
              <Link to="/register" className="btn btn-outline-secondary">
                <i className="bi bi-person-plus"></i>
                Create New Account
              </Link>
            </div>

            <div className="text-center mb-4">
              <a href="/forgot-password" className="text-decoration-none text-muted">
                Forgot your password?
              </a>
            </div>

            <div className="d-grid gap-3">
              <a 
                href="https://qure.science/qurexr/telepresence" 
                className="btn btn-telepresence"
                target="_self"
              >
                <i className="bi bi-camera-video"></i>
                Telepresence
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
